// client/src/context/GroupContext.js (Updated for Unified Chat Model)

import React, {
    createContext,
    useContext,
    useState,
    useEffect,
    useCallback
} from 'react';
import { authFetch } from '../Utilities/Api';
import AuthContext from './AuthContext';

const GroupContext = createContext();
export const useGroupContext = () => useContext(GroupContext);

const GroupProvider = ({ children }) => {
    // Note: Assuming AuthContext is imported correctly as a default export
    const { user, loading: authLoading, accessTokenExpiry } = useContext(AuthContext);

    const [groups, setGroups] = useState([]);
    const [groupInvites, setGroupInvites] = useState([]);
    const [loadingGroups, setLoadingGroups] = useState(false);
    const [loadingInvites, setLoadingInvites] = useState(false);

    // ✅ Stable version of fetchGroups, now fetching unified 'Chat' objects (type: 'GROUP')
    const fetchGroups = useCallback(async () => {
        if (!user?._id) return;
        try {
            setLoadingGroups(true);

            // ✅ CORRECT: Server route is now set for /chats/my-groups
            const res = await authFetch('/chats/my-groups');

            if (!res.ok) throw new Error(`Status ${res.status}`);

            const data = await res.json();

            if (Array.isArray(data)) {
                // The data array now holds the unified Chat objects (with name, admin, members, etc.)
                setGroups(data);
            } else {
                console.warn('⚠️ Unexpected groups response:', data);
            }
        } catch (err) {
            console.error('❌ Failed to fetch groups:', err);
        } finally {
            setLoadingGroups(false);
        }
    }, [user?._id]);

    // ✅ Stable version of fetchGroupInvites
    const fetchGroupInvites = useCallback(async () => {
        if (!user?._id) return;
        try {
            setLoadingInvites(true);

            // 🛑 FIX APPLIED: Changed to the consolidated /chats/invites route
            const res = await authFetch('/chats/invites');

            if (!res.ok) throw new Error(`Status ${res.status}`);

            const data = await res.json();

            if (Array.isArray(data)) {
                setGroupInvites(data);
            } else {
                console.warn('⚠️ Unexpected invites response:', data);
            }
        } catch (err) {
            console.error('❌ Failed to fetch group invites:', err);
        } finally {
            setLoadingInvites(false);
        }
    }, [user?._id]);

    // ✅ Effect to run fetches when user status is stable
    useEffect(() => {
        // Ensure user is defined, not loading, and token is still valid
        if (!user || authLoading || !accessTokenExpiry) return;
        fetchGroups();
        fetchGroupInvites();
    }, [user, authLoading, accessTokenExpiry, fetchGroups, fetchGroupInvites]);

    return (
        <GroupContext.Provider
            value={{
                groups,
                setGroups,
                groupInvites,
                setGroupInvites,
                fetchGroups,
                fetchGroupInvites,
                loadingGroups,
                loadingInvites
            }}
        >
            {children}
        </GroupContext.Provider>
    );
};

export default GroupProvider;